import { supabase } from '../config/supabase';
import { Event } from '../types/event.types';
import type { Tables, TablesInsert, TablesUpdate } from '../types/supabase.generated';
import type { Json } from '../types/supabase.generated';
import { smartListenerService } from './SmartListenerService';

// Types Supabase
type DbEventRow = Tables<'events'>;
type DbEventInsert = TablesInsert<'events'>;
type DbEventUpdate = TablesUpdate<'events'>;

// Interface pour les prix (stockés en JSONB)
interface PriceItem {
  amount: number;
  description: string;
  currency: string;
}

/**
 * Convertit une date et une heure de fin en string ISO
 * Format: "YYYY-MM-DDTHH:mm"
 */
export const calculateEndTimeString = (startDate: Date, endTimeStr: string): string => {
  const [hours, minutes] = endTimeStr.split(':').map(Number);
  const endDate = new Date(startDate);
  endDate.setHours(hours, minutes);

  // Si l'heure de fin est avant l'heure de début, ajouter 1 jour
  if (endDate < startDate) {
    endDate.setDate(endDate.getDate() + 1);
  }

  const year = endDate.getFullYear();
  const month = String(endDate.getMonth() + 1).padStart(2, '0');
  const day = String(endDate.getDate()).padStart(2, '0');
  const hour = String(endDate.getHours()).padStart(2, '0');
  const minute = String(endDate.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hour}:${minute}`;
};

/**
 * Calcule automatiquement endTime si non fourni (datetime + 2 heures)
 * Retourne un ISO string UTC pour stockage en TIMESTAMPTZ
 */
export const ensureEndTime = (datetime: Date, endTime?: string): string => {
  if (endTime && endTime.trim() !== '') {
    // Si c'est déjà un ISO string complet (UTC), le retourner tel quel
    if (endTime.includes('T')) {
      return endTime;
    }
    // Si c'est juste "HH:mm", utiliser calculateEndTimeString (legacy)
    return calculateEndTimeString(datetime, endTime);
  }

  // Pas d'endTime → start + 2 heures (en UTC)
  const endDate = new Date(datetime);
  endDate.setTime(endDate.getTime() + 2 * 60 * 60 * 1000);
  return endDate.toISOString();
};

/**
 * Convertit un DbEventRow (Supabase) en Event (App)
 */
function dbEventToEvent(row: DbEventRow): Event {
  return {
    id: row.id,
    organizerId: row.organizer_id,
    title: row.title,
    description: row.description ?? undefined,
    datetime: row.datetime ? new Date(row.datetime) : undefined,
    endTime: row.end_time ?? undefined,
    timezone: (row as any).timezone ?? 'Asia/Colombo',
    location: row.location ?? undefined,
    locationName: row.location_name ?? undefined,
    latitude: row.latitude ?? undefined,
    longitude: row.longitude ?? undefined,
    geohash: row.geohash ?? undefined,
    tags: row.tags ?? [],
    image: row.image ?? undefined,
    prices: (row.prices as unknown as PriceItem[]) ?? [],
    ticketingUrl: row.ticketing_url ?? undefined,
    views: row.views ?? 0,
    status: (row.status as 'active' | 'draft' | 'cancelled') ?? 'draft',
    cancelledAt: (row as any).cancelled_at ? new Date((row as any).cancelled_at) : undefined,
    cancellationReason: (row as any).cancellation_reason ?? undefined,
    createdAt: row.created_at ? new Date(row.created_at) : undefined,
    updatedAt: row.updated_at ? new Date(row.updated_at) : undefined,
    seriesId: row.series_id ?? undefined,
    isException: row.is_exception ?? false,
    weekDays: row.week_days ?? [],
    seriesFrequency: (row.series_frequency as 'daily' | 'weekly') ?? undefined,
    seriesEndType: (row.series_end_type as 'date' | 'occurrences') ?? undefined,
    seriesEndDate: row.series_end_date ? new Date(row.series_end_date) : undefined,
    seriesOccurrences: row.series_occurrences ?? undefined,
    // Import intelligent (PRD-import-intelligent-events.md)
    importConfidence: (row as any).import_confidence ?? undefined,
    importSource: (row as any).import_source ?? undefined,
  };
}

/**
 * Service Supabase pour la gestion des événements
 */
export const supabaseEventService = {
  /**
   * Crée un nouvel événement
   */
  async createEvent(eventData: Partial<Event> & { organizerId: string }): Promise<string> {
    try {
      console.log('🆕 [Supabase] Création événement:', eventData.title);

      // Valider si non-brouillon
      if (eventData.status !== 'draft' && !eventData.datetime) {
        throw new Error('Start date/time is required');
      }

      // Date par défaut pour brouillon
      if (eventData.status === 'draft' && !eventData.datetime) {
        const defaultDate = new Date();
        defaultDate.setMonth(defaultDate.getMonth() + 1);
        eventData.datetime = defaultDate;
      }

      // Assurer endTime
      if (eventData.datetime) {
        eventData.endTime = ensureEndTime(eventData.datetime, eventData.endTime);
      }

      // Construire les données pour Supabase
      // Note: || null gère les chaînes vides "" en plus de null/undefined
      const insertData: DbEventInsert & { timezone: string } = {
        organizer_id: eventData.organizerId,
        title: eventData.title || '',
        description: eventData.description || null,
        datetime: eventData.datetime?.toISOString() || null,
        end_time: eventData.endTime || null,
        timezone: eventData.timezone || 'Asia/Colombo', // 🌍 Timezone de l'événement
        location: eventData.location || null,
        location_name: eventData.locationName || null,
        latitude: eventData.latitude ?? null,
        longitude: eventData.longitude ?? null,
        geohash: eventData.geohash || null, // Geohash calculé côté client (backup: trigger Supabase)
        tags: eventData.tags ?? [],
        image: eventData.image || null,
        prices: (eventData.prices ?? []) as unknown as Json,
        ticketing_url: eventData.ticketingUrl || null,
        status: eventData.status ?? 'draft',
        series_id: eventData.seriesId || null,
        is_exception: eventData.isException ?? false,
        week_days: eventData.weekDays ?? [],
        series_frequency: eventData.seriesFrequency || null,
        series_end_type: eventData.seriesEndType || null,
        series_end_date: eventData.seriesEndDate?.toISOString() || null,
        series_occurrences: eventData.seriesOccurrences ?? null,
        // Import intelligent (PRD-import-intelligent-events.md)
        import_confidence: eventData.importConfidence ?? null,
        import_source: eventData.importSource || null,
      } as any;

      const { data, error } = await supabase
        .from('events')
        .insert(insertData)
        .select('id')
        .single();

      if (error) throw error;
      if (!data) throw new Error('No data returned from insert');

      // Notifier l'UI
      window.dispatchEvent(new CustomEvent('eventCreated', {
        detail: {
          eventId: data.id,
          organizerId: eventData.organizerId,
          status: eventData.status,
          type: 'single'
        }
      }));

      // 🔄 FORCE REFRESH: Notifier les consumers pour mise à jour immédiate de l'UI
      await smartListenerService.forceRefreshOrganizerEvents(eventData.organizerId);

      console.log('✅ [Supabase] Événement créé:', data.id);
      return data.id;
    } catch (error) {
      console.error('❌ [Supabase] Erreur création événement:', error);
      throw error;
    }
  },

  /**
   * Met à jour un événement existant
   */
  async updateEvent(eventId: string, eventData: Partial<Event>): Promise<void> {
    try {
      console.log('📝 [Supabase] Mise à jour événement:', eventId);

      // Récupérer l'organizerId pour le refresh
      let organizerId = eventData.organizerId;
      if (!organizerId) {
        const { data: event } = await supabase
          .from('events')
          .select('organizer_id')
          .eq('id', eventId)
          .single();
        organizerId = event?.organizer_id;
      }

      const updateData: DbEventUpdate = {
        updated_at: new Date().toISOString(),
      };

      // Mapper les champs
      if (eventData.title !== undefined) updateData.title = eventData.title;
      if (eventData.description !== undefined) updateData.description = eventData.description;
      if (eventData.datetime !== undefined) {
        updateData.datetime = eventData.datetime.toISOString();
        updateData.end_time = ensureEndTime(eventData.datetime, eventData.endTime);
      }
      if (eventData.endTime !== undefined) updateData.end_time = eventData.endTime;
      if (eventData.location !== undefined) updateData.location = eventData.location;
      if (eventData.locationName !== undefined) updateData.location_name = eventData.locationName;
      if (eventData.latitude !== undefined) updateData.latitude = eventData.latitude;
      if (eventData.longitude !== undefined) updateData.longitude = eventData.longitude;
      if (eventData.geohash !== undefined) updateData.geohash = eventData.geohash;
      if (eventData.tags !== undefined) updateData.tags = eventData.tags;
      if (eventData.image !== undefined) updateData.image = eventData.image;
      if (eventData.prices !== undefined) updateData.prices = eventData.prices as unknown as Json;
      if (eventData.ticketingUrl !== undefined) updateData.ticketing_url = eventData.ticketingUrl;
      if (eventData.status !== undefined) updateData.status = eventData.status;
      if (eventData.isException !== undefined) updateData.is_exception = eventData.isException;
      if (eventData.timezone !== undefined) (updateData as any).timezone = eventData.timezone; // 🌍 Timezone

      const { error } = await supabase
        .from('events')
        .update(updateData)
        .eq('id', eventId);

      if (error) throw error;

      // Notifier l'UI
      window.dispatchEvent(new CustomEvent('eventUpdated', {
        detail: { eventId, status: eventData.status }
      }));

      // 🔄 FORCE REFRESH: Notifier les consumers pour mise à jour immédiate de l'UI
      if (organizerId) {
        await smartListenerService.forceRefreshOrganizerEvents(organizerId);
      }

      console.log('✅ [Supabase] Événement mis à jour:', eventId);
    } catch (error) {
      console.error('❌ [Supabase] Erreur mise à jour événement:', error);
      throw error;
    }
  },

  /**
   * Récupère un événement par ID
   */
  async getEventById(eventId: string): Promise<Event | null> {
    try {
      console.log('🔍 [Supabase] Chargement événement:', eventId);

      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('id', eventId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null;
        throw error;
      }

      if (!data) return null;

      // Convertir et enrichir avec données organisateur
      const event = dbEventToEvent(data);

      // Récupérer les données organisateur depuis la vue publique (bypass RLS)
      if (data.organizer_id) {
        const { data: orgData } = await supabase
          .from('organizers_public')
          .select('organizer, organizer_image, subtitle')
          .eq('id', data.organizer_id)
          .single();

        if (orgData) {
          event.organizer = orgData.organizer ?? undefined;
          event.organizerImage = orgData.organizer_image ?? undefined;
          event.organizerSubtitle = orgData.subtitle ?? undefined;
        }
      }

      console.log('✅ [Supabase] Événement chargé:', eventId);
      return event;
    } catch (error) {
      console.error('❌ [Supabase] Erreur chargement événement:', error);
      throw error;
    }
  },

  /**
   * Récupère les événements d'un organisateur
   */
  async getEventsByOrganizerId(organizerId: string, activeOnly: boolean = true): Promise<Event[]> {
    try {
      console.log('🔍 [Supabase] Chargement événements organisateur:', organizerId);

      let query = supabase
        .from('events')
        .select('*')
        .eq('organizer_id', organizerId);

      if (activeOnly) {
        query = query
          .eq('status', 'active')
          .gte('datetime', new Date().toISOString());
      }

      query = query.order('datetime', { ascending: true });

      const { data, error } = await query;

      if (error) throw error;

      const events = (data ?? []).map(dbEventToEvent);
      console.log(`✅ [Supabase] ${events.length} événements chargés pour organisateur:`, organizerId);
      return events;
    } catch (error) {
      console.error('❌ [Supabase] Erreur chargement événements organisateur:', error);
      throw error;
    }
  },

  /**
   * Supprime un événement
   */
  async deleteEvent(eventId: string): Promise<void> {
    try {
      console.log('🗑️ [Supabase] Suppression événement:', eventId);

      // Récupérer l'organizerId AVANT suppression pour le refresh
      const { data: event } = await supabase
        .from('events')
        .select('organizer_id')
        .eq('id', eventId)
        .single();

      const organizerId = event?.organizer_id;

      const { error } = await supabase
        .from('events')
        .delete()
        .eq('id', eventId);

      if (error) throw error;

      // Notifier l'UI
      window.dispatchEvent(new CustomEvent('eventDeleted', {
        detail: { eventId }
      }));

      // 🔄 FORCE REFRESH: Notifier les consumers pour mise à jour immédiate de l'UI
      if (organizerId) {
        await smartListenerService.forceRefreshOrganizerEvents(organizerId);
      }

      console.log('✅ [Supabase] Événement supprimé:', eventId);
    } catch (error) {
      console.error('❌ [Supabase] Erreur suppression événement:', error);
      throw error;
    }
  },

  /**
   * Annule un événement (le garde visible avec badge CANCELLED)
   */
  async cancelEvent(eventId: string, reason?: string): Promise<void> {
    try {
      console.log('🚫 [Supabase] Annulation événement:', eventId);

      // Récupérer l'organizerId pour le refresh
      const { data: event } = await supabase
        .from('events')
        .select('organizer_id')
        .eq('id', eventId)
        .single();

      const organizerId = event?.organizer_id;

      const { error } = await supabase
        .from('events')
        .update({
          status: 'cancelled',
          cancelled_at: new Date().toISOString(),
          cancellation_reason: reason || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', eventId);

      if (error) throw error;

      // Notifier l'UI
      window.dispatchEvent(new CustomEvent('eventCancelled', {
        detail: { eventId }
      }));

      // Force refresh
      if (organizerId) {
        await smartListenerService.forceRefreshOrganizerEvents(organizerId);
      }

      console.log('✅ [Supabase] Événement annulé:', eventId);
    } catch (error) {
      console.error('❌ [Supabase] Erreur annulation événement:', error);
      throw error;
    }
  },

  /**
   * Restaure un événement annulé (le remet en 'active')
   */
  async restoreEvent(eventId: string): Promise<void> {
    try {
      console.log('♻️ [Supabase] Restauration événement:', eventId);

      // Récupérer l'organizerId pour le refresh
      const { data: event } = await supabase
        .from('events')
        .select('organizer_id')
        .eq('id', eventId)
        .single();

      const organizerId = event?.organizer_id;

      const { error } = await supabase
        .from('events')
        .update({
          status: 'active',
          cancelled_at: null,
          cancellation_reason: null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', eventId);

      if (error) throw error;

      // Notifier l'UI
      window.dispatchEvent(new CustomEvent('eventRestored', {
        detail: { eventId }
      }));

      // Force refresh
      if (organizerId) {
        await smartListenerService.forceRefreshOrganizerEvents(organizerId);
      }

      console.log('✅ [Supabase] Événement restauré:', eventId);
    } catch (error) {
      console.error('❌ [Supabase] Erreur restauration événement:', error);
      throw error;
    }
  },

  /**
   * Récupère les événements proches d'une localisation (utilise PostGIS)
   */
  async getEventsNearLocation(
    lat: number,
    lng: number,
    radiusKm: number = 10,
    minEndTime?: Date
  ): Promise<Event[]> {
    try {
      console.log(`🗺️ [Supabase] Recherche événements près de (${lat}, ${lng}), rayon ${radiusKm}km`);

      const { data, error } = await supabase.rpc('get_events_near_location', {
        p_lat: lat,
        p_lng: lng,
        p_radius_meters: radiusKm * 1000,
        p_min_end_time: minEndTime?.toISOString() ?? new Date().toISOString(),
      });

      if (error) throw error;

      const events = (data ?? []).map((row: any) => ({
        id: row.id,
        organizerId: row.organizer_id,
        title: row.title,
        description: row.description ?? undefined,
        datetime: row.datetime ? new Date(row.datetime) : undefined,
        endTime: row.end_time ?? undefined,
        location: row.location ?? undefined,
        locationName: row.location_name ?? undefined,
        latitude: row.latitude ?? undefined,
        longitude: row.longitude ?? undefined,
        tags: row.tags ?? [],
        image: row.image ?? undefined,
        prices: row.prices ?? [],
        ticketingUrl: row.ticketing_url ?? undefined,
        views: row.views ?? 0,
        status: row.status ?? 'draft',
        seriesId: row.series_id ?? undefined,
        isException: row.is_exception ?? false,
        weekDays: row.week_days ?? [],
        seriesFrequency: row.series_frequency ?? undefined,
        seriesEndType: row.series_end_type ?? undefined,
        seriesEndDate: row.series_end_date ? new Date(row.series_end_date) : undefined,
        seriesOccurrences: row.series_occurrences ?? undefined,
        distanceMeters: row.distance_meters,
      } as Event & { distanceMeters?: number }));

      console.log(`✅ [Supabase] ${events.length} événements trouvés dans un rayon de ${radiusKm}km`);
      return events;
    } catch (error) {
      console.error('❌ [Supabase] Erreur recherche géographique:', error);
      throw error;
    }
  },

  /**
   * Incrémente le compteur de vues d'un événement
   */
  async incrementViews(eventId: string): Promise<void> {
    try {
      const { error } = await supabase.rpc('increment_event_views', {
        p_event_id: eventId,
      });

      if (error) throw error;
    } catch (error) {
      console.error('❌ [Supabase] Erreur incrémentation vues:', error);
      // Ne pas throw - c'est une opération non-critique
    }
  },
};
